/**
 * Rules Admin Editor — extracted from AdminPage.jsx.
 *
 * Contents:
 *   - RulesSection: top-level tab (loads data, save/reset, gates non-super-admin)
 *   - RulesSectionEditor: per-section card UI
 *   - PendingRulesEditor: WIP "Pending Rules + Migration" block editor
 *   - Sortable*: @dnd-kit wrappers for sections + subsections (keyboard-accessible)
 *   - SectionDragPreview: floating ghost card shown via DragOverlay
 *   - DragHandle: shared grip button used at both sortable levels
 *
 * Data model note: subsections have no server-side id so we lazily backfill a
 * client-side `_uid` field. It safely round-trips through the JSON blob at
 * PUT /api/admin/content/rules.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, XCircle, RotateCcw, Trash2, Pencil, Plus, Save, GripVertical,
  ChevronUp, ChevronDown,
} from "lucide-react";
import {
  DndContext, DragOverlay, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable,
  sortableKeyboardCoordinates, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api";
import { RULES_ICON_MAP } from "@/components/site/Rules";
import { TextField, MarkdownTextArea, IconPicker, IconBtn, HexColorRow } from "./adminPrimitives";

// -------- Client-side stable ids for subsections --------

let __uidCounter = 0;
function makeUid() {
  __uidCounter += 1;
  return `uid-${Date.now().toString(36)}-${__uidCounter}`;
}

// -------- Public entry point: the "Rules" admin tab --------

export function RulesSection({ isSuperAdmin }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);
  const [icons, setIcons] = useState([]);
  const [colors, setColors] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/content/rules");
      setContent(res.data?.content || null);
      setIcons(res.data?.available_icons || []);
      setColors(res.data?.available_colors || []);
      setSeverities(res.data?.available_severities || []);
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-5 text-[#8D9A87] text-sm">
        <span className="font-mono-ui">// Only the server owner can edit the Rules page.</span>
      </div>
    );
  }

  if (loading || !content) {
    return (
      <div className="text-center py-16">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={24} />
      </div>
    );
  }

  const update = (patch) => {
    setContent((c) => ({ ...c, ...patch }));
    setDirty(true);
  };
  const updateMeta = (patch) => update({ meta: { ...content.meta, ...patch } });
  const updateWelcome = (patch) => update({ welcome: { ...content.welcome, ...patch } });
  const updateFinal = (patch) => update({ final_statement: { ...content.final_statement, ...patch } });
  const updateSections = (fn) => update({ sections: fn(content.sections) });
  const updatePending = (patch) => update({
    pending_rules: { ...(content.pending_rules || { enabled: true, heading: "Pending Rules + Migration", intro: "", items: [] }), ...patch },
  });
  const updatePendingItems = (fn) => {
    const pr = content.pending_rules || { enabled: true, heading: "Pending Rules + Migration", intro: "", items: [] };
    updatePending({ items: fn(pr.items || []) });
  };

  const addSection = () => {
    const next = [...content.sections];
    const num = String(next.length + 1).padStart(2, "0");
    next.push({
      id: `section-${Date.now()}`,
      number: num,
      title: "New Section",
      icon: "Users",
      color: colors[0] || "#C0A875",
      subsections: [{ _uid: makeUid(), title: `${next.length + 1}.1 New Subsection`, items: [{ text: "New rule.", sev: null }] }],
    });
    updateSections(() => next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/content/rules", content);
      setContent(res.data?.content || content);
      setDirty(false);
      toast.success("Rules page saved. Reload /rules to see it live.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset to the built-in defaults? Your custom content will be lost.")) return;
    setSaving(true);
    try {
      const res = await api.post("/admin/content/rules/reset");
      setContent(res.data?.content);
      setDirty(false);
      toast.success("Reset to defaults.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="rules-editor">
      {/* Sticky action bar */}
      <div className="sticky top-16 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-[#0D110C]/95 backdrop-blur-md border-b border-[#283524] flex items-center justify-between gap-3">
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
          {dirty ? (
            <span className="text-[#C0A875]">// UNSAVED CHANGES</span>
          ) : (
            <span>// UP TO DATE</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={saving}
            data-testid="rules-reset"
            className="inline-flex items-center gap-2 border border-[#5C6B51] text-[#8D9A87] hover:text-[#E2E4DF] px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] disabled:opacity-50"
          >
            <RotateCcw size={12} /> Reset defaults
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="rules-save"
            className="inline-flex items-center gap-2 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] font-semibold"
          >
            {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Meta */}
      <div className="bg-[#161D14] border border-[#283524] p-5">
        <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Page meta</h4>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
              Last updated <span className="text-[#5C6B51]">(auto-set on save)</span>
            </label>
            <input
              value={content.meta?.last_updated || ""}
              readOnly
              disabled
              data-testid="rules-meta-last-updated"
              className="w-full bg-[#0D110C]/60 border border-[#283524] text-[#8D9A87] px-3 py-2 font-mono-ui text-xs cursor-not-allowed"
            />
          </div>
          <TextField label="Server owner" value={content.meta?.server_owner || ""} onChange={(v) => updateMeta({ server_owner: v })} testid="rules-meta-server-owner" />
        </div>
      </div>

      {/* Welcome */}
      <div className="bg-[#161D14] border border-[#283524] p-5">
        <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Welcome box</h4>
        <div className="mt-4 space-y-3">
          <TextField label="Heading" value={content.welcome?.heading || ""} onChange={(v) => updateWelcome({ heading: v })} testid="rules-welcome-heading" />
          <MarkdownTextArea label="Body" rows={5} value={content.welcome?.body || ""} onChange={(v) => updateWelcome({ body: v })} testid="rules-welcome-body" />
        </div>
      </div>

      {/* Sections */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Sections</h4>
          <button
            type="button"
            onClick={addSection}
            data-testid="rules-add-section"
            className="inline-flex items-center gap-2 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em]"
          >
            <Plus size={12} /> Add section
          </button>
        </div>
        <div className="space-y-4">
          <SortableRulesSections
            sections={content.sections}
            updateSections={updateSections}
            icons={icons}
            colors={colors}
            severities={severities}
          />
        </div>
      </div>

      {/* Pending Rules + Migration */}
      <PendingRulesEditor
        pending={content.pending_rules}
        severities={severities}
        updatePending={updatePending}
        updatePendingItems={updatePendingItems}
      />

      {/* Final statement */}
      <div className="bg-[#161D14] border border-[#283524] p-5">
        <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Final statement</h4>
        <div className="mt-4 space-y-3">
          <TextField label="Heading" value={content.final_statement?.heading || ""} onChange={(v) => updateFinal({ heading: v })} testid="rules-final-heading" />
          <MarkdownTextArea label="Body" rows={5} value={content.final_statement?.body || ""} onChange={(v) => updateFinal({ body: v })} testid="rules-final-body" />
          <TextField label="Tagline" value={content.final_statement?.tagline || ""} onChange={(v) => updateFinal({ tagline: v })} testid="rules-final-tagline" />
        </div>
      </div>
    </div>
  );
}

// -------- Section card --------

function RulesSectionEditor({ section, icons, colors, severities, onChange, onDelete, dragHandleProps, isDragging }) {
  const [collapsed, setCollapsed] = useState(true);
  const Icon = RULES_ICON_MAP[section.icon] || RULES_ICON_MAP.Users;
  const accent = section.color || "#C0A875";

  const updateSubs = (fn) => onChange({ subsections: fn(section.subsections || []) });

  // Coerce whatever shape the item is in into {text, sev}.
  const asItem = (x) =>
    typeof x === "string" ? { text: x, sev: null }
    : (x && typeof x === "object") ? { text: x.text || "", sev: x.sev || null }
    : { text: "", sev: null };

  return (
    <div
      className={`bg-[#161D14] border ${isDragging ? "opacity-70 ring-2 ring-[#C0A875]" : ""}`}
      style={{ borderColor: "#283524" }}
      data-testid={`rules-section-editor-${section.id}`}
    >
      <div className="flex items-center gap-3 p-4 border-b border-[#283524]">
        <DragHandle
          {...dragHandleProps}
          testid={`rules-section-drag-${section.id}`}
          title="Drag to reorder (Space to grab, Arrows to move)"
        />
        <div className="w-9 h-9 border flex items-center justify-center shrink-0" style={{ borderColor: accent, color: accent }}>
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
            Section {section.number}
          </div>
          <div className="font-display uppercase text-[#E2E4DF] text-base sm:text-lg truncate">
            {section.title}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <IconBtn onClick={() => setCollapsed((v) => !v)} title={collapsed ? "Expand" : "Collapse"} testid={`rules-section-collapse-${section.id}`}>
            <Pencil size={12} />
          </IconBtn>
          <IconBtn onClick={onDelete} title="Delete" danger testid={`rules-section-delete-${section.id}`}><Trash2 size={12} /></IconBtn>
        </div>
      </div>

      {!collapsed && (
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <TextField label="Number" value={section.number || ""} onChange={(v) => onChange({ number: v })} />
            <div className="sm:col-span-2">
              <TextField label="Title" value={section.title || ""} onChange={(v) => onChange({ title: v })} />
            </div>
            <IconPicker label="Icon" value={section.icon} icons={icons} onChange={(v) => onChange({ icon: v })} />
          </div>
          <TextField label="Slug (URL anchor)" value={section.id || ""} onChange={(v) => onChange({ id: v.replace(/[^a-z0-9-]/gi, "-").toLowerCase() })} hint="Used for the #anchor. Keep lowercase-with-dashes." />

          <div>
            <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
              Highlight colour <span style={{ color: accent }}>■</span>
            </label>
            <div data-testid={`rules-color-swatches-${section.id}`}>
              <HexColorRow
                value={section.color || "#C0A875"}
                onChange={(c) => onChange({ color: c })}
                palette={colors}
                size="md"
                testidPrefix={`rules-color-${section.id}`}
              />
            </div>
          </div>

          <div className="border-t border-[#283524] pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">Subsections</span>
              <button
                type="button"
                onClick={() => updateSubs((arr) => [...arr, { _uid: makeUid(), title: "New subsection", items: [{ text: "", sev: null }] }])}
                data-testid={`rules-add-subsection-${section.id}`}
                className="inline-flex items-center gap-1.5 text-[#C0A875] hover:text-[#E2E4DF] font-mono-ui text-[10px] uppercase tracking-[0.25em]"
              >
                <Plus size={11} /> Add subsection
              </button>
            </div>
            <SortableSubsections
              section={section}
              accent={accent}
              colors={colors}
              severities={severities}
              updateSubs={updateSubs}
              asItem={asItem}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Pending Rules + Migration --------

function PendingRulesEditor({ pending, severities, updatePending, updatePendingItems }) {
  const pr = pending || { enabled: true, heading: "Pending Rules + Migration", intro: "", items: [] };
  const items = pr.items || [];
  const addItem = () => updatePendingItems((arr) => [...arr, { title: "New rule", body: "", sev: null }]);
  const moveItem = (idx, dir) => updatePendingItems((arr) => {
    const next = [...arr];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return arr;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  return (
    <div className="bg-[#161D14] border border-yellow-700/40 p-5" data-testid="pending-rules-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="inline-block px-3 py-1 border font-mono-ui text-[10px] tracking-[0.25em] uppercase bg-yellow-900/15 text-yellow-400/90 border-yellow-700/40">
            WIP
          </span>
          <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Pending Rules + Migration</h4>
        </div>
        <label className="inline-flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] select-none cursor-pointer">
          <input
            type="checkbox"
            checked={pr.enabled !== false}
            onChange={(e) => updatePending({ enabled: e.target.checked })}
            data-testid="pending-rules-enabled"
            className="accent-[#C0A875]"
          />
          Show on public /rules
        </label>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3">
        <TextField label="Heading" value={pr.heading || ""} onChange={(v) => updatePending({ heading: v })} testid="pending-rules-heading" />
        <MarkdownTextArea label="Intro paragraph" rows={3} value={pr.intro || ""} onChange={(v) => updatePending({ intro: v })} testid="pending-rules-intro" />
      </div>

      <div className="mt-5 border-t border-[#283524] pt-4">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">Items ({items.length})</span>
          <button
            type="button"
            onClick={addItem}
            data-testid="pending-rules-add-item"
            className="inline-flex items-center gap-1.5 text-[#C0A875] hover:text-[#E2E4DF] font-mono-ui text-[10px] uppercase tracking-[0.25em]"
          >
            <Plus size={11} /> Add rule
          </button>
        </div>
        <div className="space-y-3">
          {items.map((it, idx) => {
            const num = String(idx + 1).padStart(2, "0");
            return (
              <div key={idx} className="bg-[#0D110C] border border-[#283524] p-3 space-y-2" data-testid={`pending-rules-item-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#C0A875] shrink-0 w-8">{num}</span>
                  <input
                    value={it.title || ""}
                    onChange={(e) => updatePendingItems((arr) => arr.map((r, i) => (i === idx ? { ...r, title: e.target.value } : r)))}
                    placeholder="Rule title"
                    data-testid={`pending-rules-title-${idx}`}
                    className="flex-1 bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
                  />
                  <select
                    value={it.sev || ""}
                    onChange={(e) => {
                      const val = e.target.value || null;
                      updatePendingItems((arr) => arr.map((r, i) => (i === idx ? { ...r, sev: val } : r)));
                    }}
                    data-testid={`pending-rules-sev-${idx}`}
                    className="bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-2 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] focus:border-[#C0A875] focus:outline-none"
                  >
                    <option value="">None</option>
                    {severities.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                  <IconBtn onClick={() => moveItem(idx, -1)} disabled={idx === 0} title="Move up" testid={`pending-rules-up-${idx}`}>
                    <ChevronUp size={12} />
                  </IconBtn>
                  <IconBtn onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} title="Move down" testid={`pending-rules-down-${idx}`}>
                    <ChevronDown size={12} />
                  </IconBtn>
                  <IconBtn danger onClick={() => updatePendingItems((arr) => arr.filter((_, i) => i !== idx))} title="Delete rule" testid={`pending-rules-delete-${idx}`}>
                    <Trash2 size={12} />
                  </IconBtn>
                </div>
                <textarea
                  rows={2}
                  value={it.body || ""}
                  onChange={(e) => updatePendingItems((arr) => arr.map((r, i) => (i === idx ? { ...r, body: e.target.value } : r)))}
                  placeholder="Rule body / explanation"
                  data-testid={`pending-rules-body-${idx}`}
                  className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
                />
              </div>
            );
          })}
          {items.length === 0 && (
            <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
              // No pending rules — add one to make the block appear.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// -------- Drag-and-drop primitives --------

function DragHandle({ testid, title, attributes, listeners }) {
  return (
    <button
      type="button"
      {...attributes}
      {...listeners}
      data-testid={testid}
      title={title || "Drag to reorder"}
      className="w-7 h-7 border border-[#283524] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875] flex items-center justify-center cursor-grab active:cursor-grabbing touch-none focus:outline-none focus:border-[#C0A875] focus:text-[#C0A875]"
    >
      <GripVertical size={13} />
    </button>
  );
}

function SortableRulesSections({ sections, updateSections, icons, colors, severities }) {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragCancel = () => setActiveId(null);
  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    updateSections((arr) => {
      const oldIndex = arr.findIndex((s) => s.id === active.id);
      const newIndex = arr.findIndex((s) => s.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return arr;
      // Reorder + auto-renumber ("01", "02", …) so the display stays in sync.
      const reordered = arrayMove(arr, oldIndex, newIndex);
      return reordered.map((s, i) => ({ ...s, number: String(i + 1).padStart(2, "0") }));
    });
  };

  const activeSection = activeId ? sections.find((s) => s.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        {sections.map((sec, idx) => (
          <SortableSectionItem
            key={sec.id}
            section={sec}
            icons={icons}
            colors={colors}
            severities={severities}
            onChange={(patch) =>
              updateSections((arr) => arr.map((s, i) => (i === idx ? { ...s, ...patch } : s)))
            }
            onDelete={() => {
              if (!window.confirm(`Delete section "${sec.title}"?`)) return;
              updateSections((arr) => arr.filter((_, i) => i !== idx).map((s, i) => ({ ...s, number: String(i + 1).padStart(2, "0") })));
            }}
          />
        ))}
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeSection ? <SectionDragPreview section={activeSection} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function SectionDragPreview({ section }) {
  const Icon = RULES_ICON_MAP[section.icon] || RULES_ICON_MAP.Users;
  const accent = section.color || "#C0A875";
  return (
    <div
      className="bg-[#161D14] border shadow-2xl cursor-grabbing rotate-1 opacity-95"
      style={{ borderColor: accent, boxShadow: `0 20px 40px -10px ${accent}66` }}
      data-testid="rules-section-drag-preview"
    >
      <div className="flex items-center gap-3 p-4">
        <div className="w-7 h-7 border border-[#283524] flex items-center justify-center">
          <GripVertical size={13} className="text-[#C0A875]" />
        </div>
        <div className="w-9 h-9 border flex items-center justify-center shrink-0" style={{ borderColor: accent, color: accent }}>
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#5C6B51]">
            Section {section.number}
          </div>
          <div className="font-display uppercase text-[#E2E4DF] text-base sm:text-lg truncate">
            {section.title}
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableSectionItem({ section, icons, colors, severities, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      <RulesSectionEditor
        section={section}
        icons={icons}
        colors={colors}
        severities={severities}
        onChange={onChange}
        onDelete={onDelete}
        dragHandleProps={{ attributes, listeners }}
        isDragging={isDragging}
      />
    </div>
  );
}

function SortableSubsections({ section, accent, colors, severities, updateSubs, asItem }) {
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const subs = section.subsections || [];
    if (subs.length && subs.some((s) => !s._uid)) {
      updateSubs((arr) => arr.map((s) => (s._uid ? s : { ...s, _uid: makeUid() })));
    }
  }, [section.subsections]);

  const subs = section.subsections || [];
  const items = subs.map((s) => s._uid).filter(Boolean);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragCancel = () => setActiveId(null);
  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    updateSubs((arr) => {
      const oldIndex = arr.findIndex((s) => s._uid === active.id);
      const newIndex = arr.findIndex((s) => s._uid === over.id);
      if (oldIndex < 0 || newIndex < 0) return arr;
      return arrayMove(arr, oldIndex, newIndex);
    });
  };

  const activeSub = activeId ? subs.find((s) => s._uid === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {subs.map((sub, si) => (
            <SortableSubsection
              key={sub._uid || si}
              sub={sub}
              si={si}
              sectionId={section.id}
              accent={accent}
              colors={colors}
              severities={severities}
              updateSubs={updateSubs}
              asItem={asItem}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeSub ? <SubsectionDragPreview sub={activeSub} accent={accent} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function SubsectionDragPreview({ sub, accent }) {
  const itemCount = (sub.items || []).length;
  return (
    <div
      className="bg-[#0D110C] border shadow-2xl cursor-grabbing rotate-1 opacity-95 p-3"
      style={{ borderColor: accent, boxShadow: `0 20px 40px -10px ${accent}66` }}
      data-testid="rules-subsection-drag-preview"
    >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 border border-[#283524] flex items-center justify-center">
          <GripVertical size={13} className="text-[#C0A875]" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="font-mono-ui text-xs truncate"
            style={{ color: accent }}
          >
            {sub.title || "(untitled subsection)"}
          </div>
          <div className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51] mt-0.5">
            {itemCount} rule{itemCount === 1 ? "" : "s"}
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableSubsection({ sub, si, sectionId, accent, colors, severities, updateSubs, asItem }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sub._uid || `fallback-${si}`,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-[#0D110C] border border-[#283524] p-3 space-y-2 ${isDragging ? "opacity-70 ring-2 ring-[#C0A875]" : ""}`}
      data-testid={`rules-subsection-${sectionId}-${si}`}
    >
      <div className="flex items-center gap-2">
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          testid={`rules-subsection-drag-${sectionId}-${si}`}
          title="Drag to reorder subsection (Space to grab, Arrows to move)"
        />
        <input
          value={sub.title || ""}
          onChange={(e) => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title: e.target.value } : s)))}
          className="flex-1 bg-[#0D110C] border border-[#283524] px-3 py-2 font-mono-ui text-xs focus:outline-none"
          style={{ color: sub.title_color || accent, borderColor: "#283524" }}
          placeholder="e.g. 1.1 Respect & Conduct"
        />
        <IconBtn danger onClick={() => updateSubs((arr) => arr.filter((_, i) => i !== si))} title="Delete subsection">
          <Trash2 size={12} />
        </IconBtn>
      </div>

      {/* Title styling — size + color + underline (mirrors GuideEditor) */}
      <div
        className="flex flex-wrap items-center gap-2 pl-8 py-1.5 border-l border-[#283524]"
        data-testid={`rules-subsection-styles-${sectionId}-${si}`}
      >
        <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
          // size:
        </span>
        {[
          { key: "compact", label: "S" },
          { key: "normal", label: "M" },
          { key: "huge", label: "L" },
        ].map((opt) => {
          const active = (sub.title_size || "normal") === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() =>
                updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title_size: opt.key } : s)))
              }
              title={`Title size: ${opt.key}`}
              data-testid={`rules-subsection-size-${sectionId}-${si}-${opt.key}`}
              className={`h-6 min-w-[24px] px-1.5 border-2 font-mono-ui text-[10px] font-bold ${
                active
                  ? "border-[#E2E4DF] text-[#E2E4DF] bg-[#283524]/50"
                  : "border-[#283524] text-[#5C6B51] hover:border-[#8D9A87]"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
        <span className="w-px h-4 bg-[#283524] mx-1" />
        <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
          // color:
        </span>
        <HexColorRow
          value={sub.title_color}
          onChange={(c) => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title_color: c } : s)))}
          onClear={() => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title_color: null } : s)))}
          clearLabel="A"
          clearTitle="Use section accent"
          palette={colors}
          size="sm"
          testidPrefix={`rules-subsection-color-${sectionId}-${si}`}
        />
        <label
          className="ml-3 inline-flex items-center gap-1.5 cursor-pointer select-none"
          title="Underline the title on the public page"
        >
          <input
            type="checkbox"
            checked={!!sub.title_underline}
            onChange={(e) =>
              updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, title_underline: e.target.checked } : s)))
            }
            data-testid={`rules-subsection-underline-${sectionId}-${si}`}
            className="accent-[#C0A875]"
          />
          <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#8D9A87]">
            underline
          </span>
        </label>

        {si > 0 && (
          <>
            <div className="inline-flex items-center gap-1.5 ml-3">
              <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
                // divider:
              </span>
              <select
                value={sub.divider || "line"}
                onChange={(e) =>
                  updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, divider: e.target.value } : s)))
                }
                data-testid={`rules-subsection-divider-${sectionId}-${si}`}
                className="bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-1.5 py-1 font-mono-ui text-[10px] uppercase focus:border-[#C0A875] focus:outline-none"
              >
                <option value="line">— Line</option>
                <option value="dashed">─ ─ Dashed</option>
                <option value="dotted">· · Dotted</option>
                <option value="double">= Double</option>
                <option value="accent">━ Accent</option>
                <option value="glyph">✦ Glyph</option>
                <option value="none">✕ None</option>
              </select>
            </div>
            {(sub.divider || "line") !== "none" && (
              <div className="inline-flex items-center gap-1.5 ml-1">
                <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
                  color:
                </span>
                <HexColorRow
                  value={sub.divider_color}
                  onChange={(c) =>
                    updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, divider_color: c } : s)))
                  }
                  onClear={() =>
                    updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, divider_color: null } : s)))
                  }
                  clearLabel="A"
                  clearTitle="Auto (gray / subsection color)"
                  palette={colors}
                  size="sm"
                  testidPrefix={`rules-subsection-divider-color-${sectionId}-${si}`}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="space-y-1.5">
        {(sub.items || []).map((rawItem, ii) => {
          const item = asItem(rawItem);
          return (
            <div key={ii} className="flex items-start gap-2">
              <span className="font-mono-ui text-sm pt-1.5 select-none" style={{ color: accent }}>›</span>
              <textarea
                rows={1}
                value={item.text}
                onChange={(e) => {
                  const val = e.target.value;
                  updateSubs((arr) => arr.map((s, i) =>
                    i === si
                      ? { ...s, items: s.items.map((it, j) => (j === ii ? { ...asItem(it), text: val } : it)) }
                      : s,
                  ));
                }}
                className="flex-1 bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
                placeholder="Rule text…"
              />
              <select
                value={item.sev || ""}
                onChange={(e) => {
                  const val = e.target.value || null;
                  updateSubs((arr) => arr.map((s, i) =>
                    i === si
                      ? { ...s, items: s.items.map((it, j) => (j === ii ? { ...asItem(it), sev: val } : it)) }
                      : s,
                  ));
                }}
                data-testid={`rules-item-sev-${sectionId}-${si}-${ii}`}
                className="bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-2 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] focus:border-[#C0A875] focus:outline-none"
              >
                <option value="">None</option>
                {severities.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
              <IconBtn
                danger
                onClick={() => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, items: s.items.filter((_, j) => j !== ii) } : s)))}
                title="Delete item"
              >
                <XCircle size={12} />
              </IconBtn>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => updateSubs((arr) => arr.map((s, i) => (i === si ? { ...s, items: [...(s.items || []), { text: "", sev: null }] } : s)))}
          className="ml-5 text-[#8D9A87] hover:text-[#C0A875] font-mono-ui text-[10px] uppercase tracking-[0.25em]"
        >
          + Add item
        </button>
      </div>
    </div>
  );
}
